import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock, Download, Stethoscope } from "lucide-react";
import { formatINR, packCopy } from "../lib/constants";
import { RatingLine } from "./Rating";
import TubeArt from "./TubeArt";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const freeDownloadUrl = (slug) => `${BASE}/api/shop/digital/${slug}/file`;

const statusLabel = (p) => {
    if (p.coming_soon) return "Coming Soon";
    if (p.purchasable === false) return "Unavailable";
    return null;
};

export const ProductCard = ({ p }) => {
    const badge = statusLabel(p);
    const pack = packCopy(p);
    return (
        <Link to={`/product/${p.slug}`} data-testid={`shop-product-card-${p.slug}`}
            className="group relative flex flex-col p-5 rounded-sm border border-white/10 bg-bg1 hover:border-lime/40 hover:-translate-y-1 transition-all overflow-hidden">
            <div className="aspect-[3/4] rounded-sm bg-gradient-to-b from-white/[0.06] to-white/[0.02] flex items-center justify-center overflow-hidden relative border border-white/10">
                {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.name}
                        className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" />
                ) : (
                    <TubeArt name={(p.name.split(" ")[0] || p.name)}
                        suffix={p.name.split(" ").slice(1).join(" ")} color={p.color || "#7CB342"}
                        flavor={(p.flavor || "").toUpperCase()} unitCopy={pack.unit} count={pack.count}
                        lines={pack.lines}
                        className="w-[130px] h-auto opacity-80 group-hover:scale-105 transition-transform duration-500" />
                )}
                {badge && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/75 border border-gold/40 text-gold text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full backdrop-blur">
                        <Clock className="w-3 h-3" /> {badge}
                    </div>
                )}
            </div>
            <div className="mt-5 flex-1">
                <div className="mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
                    {[p.product_code, p.flavor || p.pack_size].filter(Boolean).join(" · ")}
                </div>
                <h3 className="font-display text-2xl uppercase tracking-tight mt-1.5">{p.name}</h3>
                {p.tagline && <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{p.tagline}</div>}
                <RatingLine product={p} className="mt-2.5" textClass="text-xs text-zinc-400" />
            </div>
            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                {p.price > 0 ? (
                    <div className="flex items-baseline gap-2">
                        <span className="font-display text-2xl" data-testid={`product-price-${p.slug}`}>{formatINR(p.price)}</span>
                        {p.compare_at_price && (
                            <span className="text-zinc-500 line-through text-xs">{formatINR(p.compare_at_price)}</span>
                        )}
                    </div>
                ) : (
                    <span className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Launching Soon</span>
                )}
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-lime">
                    View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
            </div>
        </Link>
    );
};

export const SectionHead = ({ eyebrow, title, sub, action }) => (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
        <div>
            <div className="eyebrow mb-2">{eyebrow}</div>
            <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{title}</h2>
            {sub && <p className="mt-3 text-sm text-zinc-500 max-w-xl">{sub}</p>}
        </div>
        {action}
    </div>
);

export const PackCard = ({ p }) => (
    <Link to={`/product/${p.slug}`} data-testid={`pack-card-${p.slug}`}
        className="group flex flex-col border border-white/10 bg-bg1 p-6 hover:border-lime/40 hover:-translate-y-1 transition-all">
        <Stethoscope className="w-6 h-6 text-lime" />
        <h3 className="font-display text-2xl uppercase tracking-tight mt-4">{p.name}</h3>
        {p.sessions_label && (
            <div className="mono text-[9px] tracking-[0.2em] text-gold mt-2">{p.sessions_label.toUpperCase()}</div>
        )}
        {p.tagline && <p className="text-sm text-zinc-400 mt-2 leading-relaxed line-clamp-3">{p.tagline}</p>}
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
            <div>
                {p.coming_soon ? (
                    <span className="mono text-[10px] tracking-[0.16em] text-gold inline-flex items-center gap-1.5">
                        <Clock className="w-3 h-3" /> COMING SOON
                    </span>
                ) : (
                    <>
                        <span className="font-display text-xl">{formatINR(p.price)}</span>
                        {p.compare_at_price > p.price && (
                            <span className="text-xs text-zinc-500 line-through ml-2">{formatINR(p.compare_at_price)}</span>
                        )}
                    </>
                )}
            </div>
            <span className="mono text-[10px] tracking-[0.16em] text-lime group-hover:underline">VIEW DETAILS</span>
        </div>
    </Link>
);

export const EbookCard = ({ p }) => (
    <div data-testid={`ebook-card-${p.slug}`}
        className="group flex flex-col border border-white/10 bg-bg1 hover:border-gold/40 hover:-translate-y-1 transition-all overflow-hidden">
        <Link to={`/product/${p.slug}`} className="block aspect-[3/4] bg-gradient-to-br from-gold/15 to-bg2 relative overflow-hidden">
            {p.images?.[0] ? (
                <img src={p.images[0]} alt={p.name} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
                <div className="w-full h-full grid place-items-center">
                    <BookOpen className="w-10 h-10 text-gold/70" />
                </div>
            )}
            {p.digital_free && (
                <span className="absolute top-3 left-3 bg-lime text-black mono text-[9px] tracking-[0.18em] px-2 py-1">FREE</span>
            )}
        </Link>
        <div className="p-5 flex-1 flex flex-col">
            <div className="mono text-[9px] tracking-[0.2em] text-gold">EBOOK / GUIDE</div>
            <h3 className="font-display text-xl uppercase tracking-tight mt-1.5">{p.name}</h3>
            {p.tagline && <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.tagline}</p>}
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                {p.digital_free ? (
                    <span className="mono text-[10px] tracking-[0.16em] text-lime">FREE DOWNLOAD</span>
                ) : p.coming_soon ? (
                    <span className="mono text-[10px] tracking-[0.16em] text-gold">COMING SOON</span>
                ) : (
                    <span className="font-display text-lg">{formatINR(p.price)}</span>
                )}
                {p.digital_free && p.has_digital_file ? (
                    <a href={freeDownloadUrl(p.slug)} data-testid={`ebook-download-${p.slug}`}
                        className="mono text-[10px] tracking-[0.16em] text-gold inline-flex items-center gap-1.5 hover:underline">
                        <Download className="w-3.5 h-3.5" /> DOWNLOAD
                    </a>
                ) : (
                    <Link to={`/product/${p.slug}`}
                        className="mono text-[10px] tracking-[0.16em] text-gold hover:underline">VIEW</Link>
                )}
            </div>
        </div>
    </div>
);
